// The rules an inline text editor keeps whatever it edits (docs/claude/ui.md,
// *One inline text editor*): what Enter and Escape do, and the whole look it is
// given each time it opens. DOM-free: ui/inlineText.ts owns the element and the
// life cycle, and each target (a text box, a sheet box, a table cell, a title
// block field) says only how it differs.

/** What Enter does: `commits` (one line); `shift-newline` — commits, Shift+Enter
 *  a new line (a text box); `ctrl-commits` — a new line, Ctrl/Cmd+Enter commits
 *  (a sheet box, a multi-line title field). */
export type EnterRule = 'commits' | 'ctrl-commits' | 'shift-newline';
/** What Escape does: keep what was typed, or put the text back. */
export type EscRule = 'cancels' | 'commits';
export interface KeyRules { enter: EnterRule; esc: EscRule }

/** What a key pressed in the editor does: commit, cancel, or nothing (the key
 *  types, a new line included). */
export function inlineKeyAction(key: string, mods: { shift: boolean; ctrl: boolean }, rules: KeyRules): 'cancel' | 'commit' | null {
  if (key === 'Escape') return rules.esc === 'commits' ? 'commit' : 'cancel';
  if (key !== 'Enter') return null;
  switch (rules.enter) {
    case 'commits': return 'commit';
    case 'shift-newline': return mods.shift ? null : 'commit';
    case 'ctrl-commits': return mods.ctrl ? 'commit' : null;
  }
}

/** Every style property any target gives the editor. */
export const EDITOR_LOOK_KEYS = [
  'background',
  'border',
  'borderRadius',
  'caretColor',
  'color',
  'fontFamily',
  'fontSize',
  'height',
  'left',
  'lineHeight',
  'padding',
  'textAlign',
  'top',
  'transform',
  'transformOrigin',
  'whiteSpace',
  'width',
] as const;
export type LookKey = typeof EDITOR_LOOK_KEYS[number];
export type EditorLook = Partial<Record<LookKey, string>>;

/** A target's look made whole: every property any target sets, the ones this
 *  target leaves out reset to the stylesheet's (''). Applied whole, one target's
 *  look can never leak into the next one's — a sheet box's transparent text once
 *  stayed on a text box opened after it, kept away only by a hand-written reset. */
export function editorStyle(look: EditorLook): Record<LookKey, string> {
  const out = {} as Record<LookKey, string>;
  for (const k of EDITOR_LOOK_KEYS) out[k] = look[k] ?? '';
  return out;
}
